import React from 'react';
import { Container, Body5 } from '@ui';
import { NodeParameterRemoveButton } from '@flow/buttons/NodeParameterRemoveButton';
import { AgentFlowParameterWrapper, AgentFlowParameterLabelWrapper, AgentFlowSVGIconButton } from '@flow/styles';
import { TransparentFlatButton } from '@ui/buttons';
import { isValidJsonObject, getKey, getValue } from '@utils/usefulFuncs';

function generateFixedKeys(items, fixedKeys, value) {
  const usedKeys = items.map((item) => getKey(item));
  const filteredKeys = fixedKeys.filter((key) => !usedKeys.includes(key));
  if (typeof value === 'string' && value.length > 0) {
    filteredKeys.push(value);
  }
  return filteredKeys.map((key) => (
    <option key={key} value={key}>
      {key}
    </option>
  ));
}

const ObjectInput = ({ index, fixedKeys, items, value, onKeyChange, onValueChange }) => {
  if (typeof value === 'string') {
    return (
      <Container fullWidth row verticallyCenter>
        <Body5>Invalid value</Body5>
      </Container>
    );
  }

  const handleKeyChange = (e) => {
    onKeyChange(e, index);
  };
  const handleValueChange = (e) => {
    onValueChange(e, index);
  };

  const availableKeys = generateFixedKeys(items, fixedKeys, getKey(value));
  if (availableKeys.length === 0) {
    return (
      <Container fullWidth row verticallyCenter>
        <Body5>No additional available items for this param</Body5>
      </Container>
    );
  }
  return (
    <Container fullWidth>
      <select value={getKey(value)} onChange={handleKeyChange}>
        <option value={''}>Please select...</option>
        {availableKeys}
      </select>
      <Container fullWidth left={16} top={8}>
        <textarea value={getValue(value)} onChange={handleValueChange} />
      </Container>
    </Container>
  );
};

const MixedListItem = ({ index, value, items, onChange, onKeyChange, onValueChange, onRemoveItemClick }) => {
  const [isString, setIsString] = React.useState(true);
  const [fixedKeys] = React.useState(['query', 'context_query']);

  React.useEffect(() => {
    setIsString(!isValidJsonObject(value));
  }, []);

  const handleRemove = () => {
    onRemoveItemClick(index);
  };

  const handleChangeToString = () => {
    onChange({ target: { value: '' } }, index);
    setIsString(true);
  };
  const handleChangeToJSON = () => {
    onChange({ target: { value: { '': '' } } }, index);
    setIsString(false);
  };

  const handleChange = (e) => {
    onChange(e, index);
  };

  return (
    <li key={index}>
      <Container fullWidth row verticallyCenter>
        {isString ? (
          <textarea value={value} onChange={handleChange} />
        ) : (
          <ObjectInput
            index={index}
            fixedKeys={fixedKeys}
            items={items}
            value={value}
            onKeyChange={onKeyChange}
            onValueChange={onValueChange}
          />
        )}
        {isString ? (
          <AgentFlowSVGIconButton onClick={handleChangeToJSON} size="18px">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M5,3H7V5H5V10A2,2 0 0,1 3,12A2,2 0 0,1 5,14V19H7V21H5C3.93,20.73 3,20.1 3,19V15A2,2 0 0,0 1,13H0V11H1A2,2 0 0,0 3,9V5A2,2 0 0,1 5,3M19,3A2,2 0 0,1 21,5V9A2,2 0 0,0 23,11H24V13H23A2,2 0 0,0 21,15V19A2,2 0 0,1 19,21H17V19H19V14A2,2 0 0,1 21,12A2,2 0 0,1 19,10V5H17V3H19M12,15A1,1 0 0,1 13,16A1,1 0 0,1 12,17A1,1 0 0,1 11,16A1,1 0 0,1 12,15M8,15A1,1 0 0,1 9,16A1,1 0 0,1 8,17A1,1 0 0,1 7,16A1,1 0 0,1 8,15M16,15A1,1 0 0,1 17,16A1,1 0 0,1 16,17A1,1 0 0,1 15,16A1,1 0 0,1 16,15Z" />
            </svg>
          </AgentFlowSVGIconButton>
        ) : (
          <AgentFlowSVGIconButton onClick={handleChangeToString} size="19px">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M5,3C3.89,3 3,3.89 3,5V19C3,20.11 3.89,21 5,21H19C20.11,21 21,20.11 21,19V5C21,3.89 20.11,3 19,3H5M5,5H19V19H5V5M7,7V9H17V7H7M7,11V13H17V11H7M7,15V17H14V15H7Z" />
            </svg>
          </AgentFlowSVGIconButton>
        )}
        <NodeParameterRemoveButton onClick={handleRemove} />
      </Container>
    </li>
  );
};

export const MixedListParameter = ({ data, value, onRemove, onChange }) => {
  const { keyName, label } = data;

  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    const syncItems = () => {
      setItems(value);
    };
    syncItems();
  }, []);

  const handleAddItem = () => {
    setItems((prevItems) => [...prevItems, '']);
  };
  const handleChange = (e, i) => {
    const value = e.target.value;
    setItems((prevItems) => {
      const copy = [...prevItems];
      copy[i] = value;
      return copy;
    });
  };
  const handleKeyChange = (e, i) => {
    const newKey = e.target.value;

    setItems((prevItems) => {
      const copy = [...prevItems];
      const entry = { ...copy[i] };
      const updatedItem = { [newKey]: getValue(entry) };
      copy[i] = updatedItem;
      return copy;
    });
  };
  const handleValueChange = (e, i) => {
    const textValue = e.target.value;
    setItems((prevItems) => {
      const copy = [...prevItems];
      const entry = { ...copy[i] };
      const updatedItem = { [getKey(entry)]: textValue };
      copy[i] = updatedItem;
      return copy;
    });
  };
  const handleRemoveClick = () => {
    onRemove(keyName);
  };
  const handleRemoveItemClick = (i) => {
    setItems((prevItems) => {
      const copy = [...prevItems];
      return copy.filter((el, elIndex) => elIndex !== i);
    });
  };

  return (
    <AgentFlowParameterWrapper>
      <Container fullWidth>
        <Container row verticallyCenter spaceBetween fullWidth>
          <Container row verticallyCenter>
            <AgentFlowParameterLabelWrapper>
              <Body5>{label}:</Body5>
            </AgentFlowParameterLabelWrapper>
            <div className="read-only-hide">
              <TransparentFlatButton onClick={handleAddItem}>Add List Item</TransparentFlatButton>
            </div>
          </Container>

          <NodeParameterRemoveButton onClick={handleRemoveClick} />
        </Container>
        <ul>
          {items.map((item, index) => (
            <MixedListItem
              key={index}
              index={index}
              value={item}
              items={items}
              onChange={handleChange}
              onKeyChange={handleKeyChange}
              onValueChange={handleValueChange}
              onRemoveItemClick={handleRemoveItemClick}
            />
          ))}
        </ul>
      </Container>
    </AgentFlowParameterWrapper>
  );
};
